import type {
  CustomerOrderState,
  CustomerOrderStatus,
  ShipmentDeliveryIssue,
  ShipmentHandoverIssue,
  ShipmentHandoverState,
  ShipmentOnecDiscrepancy,
  ShipmentStatus,
} from '@/api';
import type { BadgeTone } from '@/ui';

/*
 * Подписи и тона отгрузки. Сами статусы приходят из API - канон жизненного цикла
 * заявки (business/processes/to-be/order-lifecycle.md), - здесь только то, как их
 * показать. Тона - из палитры макета: имена тонов совпадают со статусами макета,
 * поэтому соответствие задано явно.
 */

export interface StatusMeta {
  label: string;
  tone: BadgeTone;
}

export const SHIPMENT_STATUSES: Record<ShipmentStatus, StatusMeta> = {
  Planned1: { label: 'Запланировано', tone: 'plan' },
  Planned2: { label: 'Согласование', tone: 'approval' },
  Agreed: { label: 'Подготовка', tone: 'preparing' },
  Delivery: { label: 'Доставка', tone: 'delivery' },
  Payment: { label: 'Оплата', tone: 'payment' },
  Completed: { label: 'Завершено', tone: 'completed' },
  Returned: { label: 'Возврат', tone: 'returned' },
  Cancelled: { label: 'Отменено', tone: 'cancelled' },
};

/** Все статусы в порядке жизненного цикла - для селекторов. */
export const ALL_SHIPMENT_STATUSES: ReadonlyArray<ShipmentStatus> = [
  'Planned1',
  'Planned2',
  'Agreed',
  'Delivery',
  'Payment',
  'Completed',
  'Returned',
  'Cancelled',
];

/** Закрытые - завершённые, возвраты и отмены: их показывает «История отгрузок». */
export const FINISHED_SHIPMENT_STATUSES: ReadonlyArray<ShipmentStatus> = [
  'Completed',
  'Returned',
  'Cancelled',
];

/** Активные - всё, что ещё не закрыто; как «Активные» в списке отгрузок макета. */
export const ACTIVE_SHIPMENT_STATUSES: ReadonlyArray<ShipmentStatus> =
  ALL_SHIPMENT_STATUSES.filter(
    (status) => !FINISHED_SHIPMENT_STATUSES.includes(status),
  );

/**
 * Расхождение с заказом 1С - словами для менеджера (ADR-012, п. 5). Статус из 1С
 * двигается только вперёд (ADR-013); всё, что вперёд не ложится, показывается так.
 */
export const ONEC_DISCREPANCIES: Record<ShipmentOnecDiscrepancy, string> = {
  OrderNotPosted: 'Заказ в 1С снят с проведения',
  OrderStateBehind:
    'Отгрузка ушла дальше заказа: в 1С он снова не отгружен - похоже, отменили реализацию',
  OrderRestored:
    'Отгрузка отменена по удалению заказа, а заказ в 1С снова действует',
  OrderDeletedAfterCompletion:
    'Заказ в 1С удалён или помечен на удаление, а отгрузка уже завершена',
  UnknownProducts:
    'Часть строк заказа не вошла в отгрузку: их товара нет в справочнике',
  ForeignMarker:
    'В комментарии заказа - маркер другой отгрузки: заказ скопировали в 1С',
};

/** Передача в 1С - отдельное измерение поверх статуса; «не передавалась» не показывается. */
export const HANDOVER_STATES: Record<ShipmentHandoverState, StatusMeta | null> =
  {
    None: null,
    InFlight: { label: 'Ожидаем 1С', tone: 'approval' },
    NoResponse: { label: 'Нет ответа от 1С', tone: 'delayed' },
    Rejected: { label: 'Отказ 1С', tone: 'delayed' },
  };

/** Что мешает передать отгрузку в 1С - словами для менеджера. */
export const HANDOVER_ISSUES: Record<ShipmentHandoverIssue, string> = {
  NotInWork: 'Отгрузка не в работе: передать можно из «Согласования»',
  HandoverInProgress: 'Передача в 1С уже идёт',
  NoCounterparty: 'У клиента нет действующего контрагента',
  NoContract: 'Не выбран договор клиента',
  ContractNotUsable:
    'Договор закрыт или удалён в 1С - выберите другой договор клиента',
  ContractWithoutBankAccount:
    'В договоре нет банковского счёта организации - попросите бухгалтера заполнить его в 1С',
  NoLines: 'В отгрузке нет позиций',
  LinesWithoutPrice: 'Не во всех позициях указана цена',
  PlannedDateInPast: 'Плановая дата в прошлом',
  NoWarehouse: 'Не выбран склад отправки',
  ClientNotAgreed: 'Не отмечено, что клиент согласовал дату, объём и адрес',
  StockNotConfirmed: 'Не отмечено, что остаток на складе проверен',
  NotReconfirmed: 'Не отмечено, что клиент финально подтвердил отгрузку',
};

/** Что мешает «Передать в доставку» - словами для менеджера (ADR-013). */
export const DELIVERY_ISSUES: Record<ShipmentDeliveryIssue, string> = {
  NotPreparing: 'Передать в доставку можно из «Подготовки»',
  NoPostedOrder: 'Заказа клиента в 1С нет или он не проведён',
  OrderAwaitingApproval: 'Заказ в 1С ждёт согласования',
  OrderAwaitingPrepayment: 'Заказ в 1С ждёт предоплату',
  OrderAwaitingReserve: 'Заказ в 1С ждёт обеспечения: товар ещё не в резерве',
  OrderStateUnknown: 'Состояние заказа 1С ещё не рассчитала',
};

/**
 * Код отказа 1С - словами для менеджера и что делать. Коды - из контракта
 * architecture/contracts/onec-commands.md; незнакомый код показывается как есть.
 */
export const HANDOVER_REJECTIONS: Readonly<Record<string, string>> = {
  validation: '1С не приняла заказ. Исправьте данные и отправьте снова.',
  unknownReference:
    'Клиент, контрагент или товар не найден в 1С или помечен на удаление.',
  expired:
    'Срок команды истёк: 1С не была доступна сутки. Проверьте отгрузку и отправьте снова.',
  technical:
    'Сбой 1С при создании заказа. Если повторится - сообщите администратору 1С.',
  invalidCommand:
    '1С отклонила команду как некорректную - это ошибка Culture, сообщите разработчикам.',
  unknownCommand: 'Расширение 1С не знает эту команду - его нужно обновить.',
};

/** Состояние заказа клиента в 1С - отгружен ли, оплачен ли; подписи как в интерфейсе УТ. */
export const CUSTOMER_ORDER_STATES: Record<CustomerOrderState, string> = {
  AwaitingApproval: 'Ожидается согласование',
  AwaitingPrepaymentBeforeReserve: 'Ожидается оплата до обеспечения',
  ReadyToReserve: 'Готов к обеспечению',
  AwaitingPrepaymentBeforeShipment: 'Ожидается оплата до отгрузки',
  AwaitingReserve: 'Ожидается обеспечение',
  ReadyToShip: 'Готов к отгрузке',
  Shipping: 'В процессе отгрузки',
  AwaitingPaymentAfterShipment: 'Ожидается оплата',
  ReadyToClose: 'Готов к закрытию',
  Closed: 'Закрыт',
  AwaitingOwnershipTransfer: 'Ожидается переход права собственности',
};

/** Статус заказа клиента в 1С - подписи как в интерфейсе УТ. */
export const CUSTOMER_ORDER_STATUSES: Record<CustomerOrderStatus, string> = {
  NotAgreed: 'Не согласован',
  ToFulfil: 'К выполнению / В резерве',
  ToShip: 'К отгрузке',
  Closed: 'Закрыт',
};
